import * as React from 'react'
import { getCubeAPI } from '../../../../../../Cube'
import { QueryRenderer } from '@cubejs-client/react'
import { Skeleton } from 'antd'
import { Chart, Axis, Tooltip, Geom } from 'bizcharts'
import { parseAndDownloadCSV } from '../../../../../../utilities/helpers'
import { UncontrolledButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap'
import moment from 'moment'

interface Props {
  companyId: number
  startDate: string
  endDate: string
  locationIds: number[]
}

type timeGroup = 'hour' | 'day' | 'week' | 'month'

interface State {
  groupBy: timeGroup
  fullQuery: any
}

const query = {
  measures: ['PaymentLines.productNet'],
  timeDimensions: [],
  dimensions: [`Categories.parentname`],
  filters: [
    {
      dimension: 'Payments.status',
      operator: 'equals',
      values: [`completed`],
    },
  ],
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
}

export class CategoryTimeGraph extends React.Component<Props, State> {
  // Static property that stores loaded report data
  data: any

  constructor(props: Props) {
    super(props)
    const daysDiff = moment(props.endDate).diff(props.startDate, 'day')
    let defaultGroup: timeGroup = 'day'
    if (daysDiff > 90) {
      defaultGroup = 'month'
    } else if (daysDiff >= 1) {
      defaultGroup = 'day'
    } else {
      defaultGroup = 'hour'
    }
    this.state = {
      groupBy: defaultGroup,
      fullQuery: undefined,
    }
  }

  componentDidMount = () => this.setQuery()

  setQuery = () => {
    const { startDate, endDate, locationIds } = this.props

    const fullQuery = {
      ...query,
      timeDimensions: [
        {
          dimension: 'Payments.completedate',
          granularity: this.state.groupBy,
          dateRange: [startDate, endDate],
        },
      ],
      filters: [
        ...query.filters,
        {
          dimension: 'Locations.id',
          operator: 'equals',
          values: locationIds.map((id) => `${id}`),
        },
      ],
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }

    this.setState({ fullQuery })
  }

  stackedChartData = (resultSet: any) => {
    // X-axis should show different values based on the group by
    let format: string
    const { groupBy } = this.state
    if (groupBy === 'hour') {
      format = 'MMM D ha'
    } else if (groupBy === 'month') {
      format = 'MMM'
    } else {
      format = 'YYYY MMM D'
    }

    const data = resultSet
      .pivot()
      .map(({ xValues, yValuesArray }: any) => {
        // Change the Y axis title from "PaymentLines.net" to "Net sales"
        yValuesArray.forEach((y: any) => {
          y[0][1] = 'Net sales'
        })
        return yValuesArray.map(([yValues, m]: any) => ({
          // For hourly graphs, include the hours
          x: moment(resultSet.axisValuesString(xValues, ', ')).format(format),
          color: resultSet.axisValuesString(yValues, ', '),
          measure: m && Number.parseFloat(m),
        }))
      })
      .reduce((a: any, b: any) => a.concat(b))

    return data
  }

  barRender = ({ resultSet }: any) => {
    return (
      <Chart
        height={200}
        scale={{ x: { tickCount: 8 } }}
        data={this.stackedChartData(resultSet)}
        forceFit
        padding={{ top: 20, right: 30, bottom: 20, left: 60 }}
      >
        <Axis name='x' />
        <Axis name='measure' />
        <Tooltip follow />
        <Geom type='intervalStack' position={`x*measure`} color='color' />
      </Chart>
    )
  }

  renderChart(Component: any) {
    return ({ resultSet, error }: any) => {
      if (resultSet) {
        this.data = resultSet.rawData()
      } else {
        this.data = undefined
      }
      return (
        (resultSet && <Component resultSet={resultSet} />) ||
        (error && error.toString()) || <Skeleton active />
      )
    }
  }

  exportCSV() {
    const { startDate, endDate } = this.props
    const data = this.data
    if (data) {
      parseAndDownloadCSV(`category-net-sales-${startDate}-${endDate}`, data)
    }
  }

  render() {
    const { companyId, startDate, endDate } = this.props
    const { fullQuery } = this.state
    const daysDiff = moment(this.props.endDate).diff(this.props.startDate, 'day')

    return (
      <div>
        <div>
          <b>Category net sales</b>
        </div>
        <div className='text-muted'>Over time</div>

        <div className='mt-2 mb-2'>
          <UncontrolledButtonDropdown className='mr-2'>
            <DropdownToggle caret size='sm' color='light' style={{ textAlign: 'left' }}>
              {this.state.groupBy}
            </DropdownToggle>
            <DropdownMenu>
              {daysDiff < 1 && (
                <DropdownItem onClick={() => this.setState({ groupBy: 'hour' }, this.setQuery)}>
                  hour
                </DropdownItem>
              )}
              {daysDiff >= 1 && (
                <DropdownItem onClick={() => this.setState({ groupBy: 'day' }, this.setQuery)}>
                  day
                </DropdownItem>
              )}
              {daysDiff >= 30 && (
                <DropdownItem onClick={() => this.setState({ groupBy: 'week' }, this.setQuery)}>
                  week
                </DropdownItem>
              )}
              {daysDiff >= 60 && (
                <DropdownItem onClick={() => this.setState({ groupBy: 'month' }, this.setQuery)}>
                  month
                </DropdownItem>
              )}
            </DropdownMenu>
          </UncontrolledButtonDropdown>
          <button className='btn btn-light btn-sm' onClick={() => this.exportCSV()}>
            Export as csv
          </button>
        </div>
        <div>
          {fullQuery ? (
            <QueryRenderer
              query={fullQuery}
              cubejsApi={getCubeAPI(companyId)}
              render={this.renderChart(this.barRender)}
            />
          ) : (
            <Skeleton active />
          )}
        </div>
      </div>
    )
  }
}
